import React, { useState } from "react";
import api, { generateReport } from "../services/api";
import { usePoll } from "../lib/hooks";
import { fmtDateTime, plural } from "../lib/format";
import { Badge, Button, Callout, Card, Empty, ErrorState, Field, LinkButton, Segmented, SkeletonRows, cx, useToast } from "../components/ui";
import { Icon } from "../components/Icon";
import { PageHeader } from "../components/ConsoleShell";

// The type sets the document's title; every type currently carries the same sections.
const TYPES = [
  { id: "executive", icon: "layers", label: "Executive summary", body: "For leadership" },
  { id: "daily", icon: "calendar", label: "Daily security summary", body: "For the shift log" },
  { id: "weekly", icon: "trendUp", label: "Weekly threat intelligence", body: "For the weekly review" },
  { id: "incident", icon: "alert", label: "Incident response", body: "For an incident file" },
  { id: "mitre", icon: "target", label: "MITRE ATT&CK coverage", body: "For threat-model reviews" },
  { id: "compliance", icon: "clipboard", label: "Compliance (ISO 27001 / NIST)", body: "For auditors, as a self-assessment" },
  { id: "full", icon: "fileText", label: "Full security report", body: "For the project archive" },
];
const CONTENTS = [
  "Event, threat and alert totals for the period",
  "Severity and attack-type breakdowns",
  "Top origin countries",
  "The 20 highest-severity events",
  "MITRE ATT&CK coverage, when techniques were observed",
  "Machine-learning and deep-learning model metrics",
];
const TYPE_LABEL = Object.fromEntries(TYPES.map((t) => [t.id, t.label]));
const PERIODS = [
  { id: 24, label: "24 hours" },
  { id: 168, label: "7 days" },
  { id: 720, label: "30 days" },
];
const SCHEDULES = [
  { id: "0 8 * * *", label: "Every day at 08:00" },
  { id: "0 8 * * 1", label: "Every Monday at 08:00" },
  { id: "0 8 1 * *", label: "First day of each month at 08:00" },
];
const scheduleLabel = (s) => SCHEDULES.find((x) => x.id === s)?.label || s;
const EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

function Generate() {
  const toast = useToast();
  const [type, setType] = useState("executive");
  const [hours, setHours] = useState(24);
  const [fmt, setFmt] = useState("pdf");
  const [busy, setBusy] = useState(false);

  const run = async () => {
    setBusy(true);
    try {
      const r = await generateReport(type, fmt, hours);
      const cd = r.headers?.["content-disposition"] || "";
      const name = /filename="?([^";]+)"?/.exec(cd)?.[1] || `cybershield-${type}.${fmt}`;
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = name;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 2000);
      toast("Report ready", "ok", { detail: name });
    } catch (e) {
      let detail = e.message;
      try {
        const txt = await e?.response?.data?.text?.();
        detail = JSON.parse(txt).detail || detail;
      } catch {
        /* keep message */
      }
      toast("Report generation failed", "critical", { detail });
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="grid cols-12">
    <Card className="span-8" title="Generate a report" subtitle="Built from the detection database at the moment you click" icon="fileText">
      <div className="field-label" style={{ marginBottom: 10 }}>
        Title and audience
      </div>
      <div className="grid" role="radiogroup" aria-label="Report type" style={{ gridTemplateColumns: "repeat(auto-fill, minmax(220px, 1fr))", gap: 10 }}>
        {TYPES.map((t) => (
          <button
            key={t.id}
            type="button"
            role="radio"
            aria-checked={type === t.id}
            className={cx("card-inset")}
            onClick={() => setType(t.id)}
            style={{
              textAlign: "left",
              color: "inherit",
              display: "flex",
              gap: 12,
              borderColor: type === t.id ? "var(--brand)" : undefined,
              background: type === t.id ? "var(--brand-wash)" : undefined,
              boxShadow: type === t.id ? "0 0 0 1px var(--brand) inset" : undefined,
              transition: "border-color 120ms, background 120ms",
            }}
          >
            <span className="stat-icon tone-brand" style={{ flexShrink: 0 }}>
              <Icon name={t.icon} size={15} />
            </span>
            <span>
              <strong style={{ display: "block", fontSize: 13.5 }}>{t.label}</strong>
              <span className="small muted">{t.body}</span>
            </span>
          </button>
        ))}
      </div>
      <div className="row row-wrap" style={{ marginTop: 20, gap: 24, alignItems: "flex-end" }}>
        <div className="field">
          <span className="field-label">Period</span>
          <Segmented label="Report period" value={hours} onChange={setHours} options={PERIODS} />
        </div>
        <div className="field">
          <span className="field-label">Format</span>
          <Segmented
            label="Report format"
            value={fmt}
            onChange={setFmt}
            options={[
              { id: "pdf", label: "PDF" },
              { id: "docx", label: "Word (.docx)" },
            ]}
          />
        </div>
        <div className="grow" />
        <Button variant="primary" size="lg" icon="download" loading={busy} onClick={run}>
          {busy ? "Generating…" : "Generate report"}
        </Button>
      </div>
    </Card>
    <Card className="span-4" title="Every report contains" icon="list" style={{ alignSelf: "start" }}>
      <ul className="check-list">
        {CONTENTS.map((t) => (
          <li key={t}>{t}</li>
        ))}
      </ul>
      <p className="small muted" style={{ margin: "14px 0 0" }}>
        The type sets the document title and audience; the sections are the same for every type in this build.
      </p>
    </Card>
    </div>
  );
}

function Scheduled() {
  const toast = useToast();
  const list = usePoll("/api/reports/scheduled");
  const [form, setForm] = useState({ name: "", report_type: "executive", schedule: SCHEDULES[0].id, recipients: "", fmt: "pdf" });
  const [busy, setBusy] = useState(false);
  const emails = form.recipients
    .split(/[,;\s]+/)
    .map((s) => s.trim())
    .filter(Boolean);
  const badEmail = emails.find((e) => !EMAIL.test(e));

  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/api/reports/scheduled", { ...form, name: form.name.trim(), recipients: emails });
      toast("Schedule saved", "ok");
      setForm((f) => ({ ...f, name: "", recipients: "" }));
      list.reload();
    } catch (err) {
      toast("Couldn't save the schedule", "critical", { detail: err?.response?.data?.detail?.[0]?.msg || err?.response?.data?.detail || err.message });
    } finally {
      setBusy(false);
    }
  };

  const items = list.data?.items;
  return (
    <div className="grid cols-12">
      <Card className="span-5" title="New schedule" icon="calendar" style={{ alignSelf: "start" }}>
        <form onSubmit={save} className="col" style={{ gap: 14 }}>
          <Field label="Name">
            <input className="input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="e.g. Monday leadership brief" required maxLength={80} />
          </Field>
          <Field label="Report">
            <select className="select" value={form.report_type} onChange={(e) => setForm({ ...form, report_type: e.target.value })}>
              {TYPES.map((t) => (
                <option key={t.id} value={t.id}>
                  {t.label}
                </option>
              ))}
            </select>
          </Field>
          <Field label="When">
            <select className="select" value={form.schedule} onChange={(e) => setForm({ ...form, schedule: e.target.value })}>
              {SCHEDULES.map((s) => (
                <option key={s.id} value={s.id}>
                  {s.label}
                </option>
              ))}
            </select>
          </Field>
          <Field label="Recipients" hint={badEmail ? `“${badEmail}” is not a valid address` : "Separate addresses with commas"}>
            <input
              className={cx("input", badEmail && "is-invalid")}
              value={form.recipients}
              onChange={(e) => setForm({ ...form, recipients: e.target.value })}
              placeholder="soc@company.com, ciso@company.com"
              required
            />
          </Field>
          <div className="field">
            <span className="field-label">Format</span>
            <Segmented
              label="Scheduled report format"
              value={form.fmt}
              onChange={(v) => setForm({ ...form, fmt: v })}
              options={[
                { id: "pdf", label: "PDF" },
                { id: "docx", label: "Word" },
              ]}
            />
          </div>
          <Button type="submit" variant="primary" icon="plus" loading={busy} disabled={!form.name.trim() || !emails.length || !!badEmail}>
            Save schedule
          </Button>
        </form>
      </Card>
      <div className="span-7 stack">
        <Callout tone="medium" icon="info" title="Delivery is not connected yet">
          Schedules are stored on the server, but automatic e-mail delivery is not active in this build. Generate reports on demand above.
        </Callout>
        <Card flush title="Saved schedules" subtitle={items ? plural(items.length, "schedule") : ""} icon="list">
          {list.error ? (
            <ErrorState detail={list.error} onRetry={list.reload} />
          ) : !items ? (
            <div className="card-body">
              <SkeletonRows rows={3} />
            </div>
          ) : !items.length ? (
            <Empty compact icon="calendar">
              No schedules yet.
            </Empty>
          ) : (
            <div className="list">
              {items.map((r) => (
                <div key={r.id} className="list-item" style={{ gridTemplateColumns: "auto minmax(0,1fr) auto" }}>
                  <span className="sev-glyph tone-brand">
                    <Icon name="calendar" size={15} />
                  </span>
                  <span style={{ minWidth: 0 }}>
                    <span className="list-title" style={{ display: "block" }}>
                      {r.name}
                    </span>
                    <span className="list-meta">
                      {TYPE_LABEL[r.type] || r.type} · {scheduleLabel(r.schedule)}
                    </span>
                  </span>
                  <span className="list-side">
                    <Badge tone="neutral">{String(r.format || "pdf").toUpperCase()}</Badge>
                    <span>{r.last_sent ? `Sent ${fmtDateTime(r.last_sent)}` : "Not sent yet"}</span>
                  </span>
                </div>
              ))}
            </div>
          )}
        </Card>
      </div>
    </div>
  );
}

export default function Reports() {
  const [view, setView] = useState("generate");
  return (
    <>
      <PageHeader
        eyebrow="Workspace"
        icon="fileText"
        title="Reports"
        description="Create security summaries, incident reports, and compliance assessments."
        actions={
          <>
            <LinkButton href="/api/reports/summary?period=day" target="_blank" rel="noreferrer" icon="download" variant="ghost">
              24-hour summary
            </LinkButton>
            <LinkButton href="/api/reports/summary?period=week" target="_blank" rel="noreferrer" icon="download" variant="ghost">
              7-day summary
            </LinkButton>
          </>
        }
      />
      <div style={{ marginBottom: 20 }}>
        <Segmented
          label="Reports view"
          value={view}
          onChange={setView}
          options={[
            { id: "generate", label: "Generate" },
            { id: "scheduled", label: "Scheduled" },
          ]}
        />
      </div>
      {view === "generate" ? <Generate /> : <Scheduled />}
    </>
  );
}
